<template>
  <VDialog v-model="isShowModal" max-width="700">
    <VToolbar color="primary" density="compact">
      <VToolbarTitle>
        <VIcon icon="mdi-file-document-multiple" class="mr-2" />
        View Document
      </VToolbarTitle>
      <VSpacer />
      <VBtn icon="mdi-close" variant="text" @click="handleClose()" />
    </VToolbar>
    <VCard>
      <VCardItem>
        <VCardText>
          <div v-if="isLoading" class="d-flex flex-column align-center justify-center py-4 w-100">
            <VProgressCircular indeterminate color="primary" />
          </div>
          <div v-else-if="documentUrl" class="d-flex flex-column align-center justify-center py-4 w-100">
            <iframe v-if="isPdf" :src="documentUrl" width="100%" height="500" frameborder="0"></iframe>
            <img
              v-else-if="isImage"
              :src="documentUrl"
              class="d-block mx-auto"
              style="max-width: 100%; max-height: 500px"
            />
            <p v-else>Cannot preview this file type. <a :href="documentUrl" download>Download</a></p>
          </div>
          <div v-else>No document to display.</div>
          <VCol cols="12">
            <VBtn
              v-if="documentUrl"
              :loading="isDownloadLoading"
              class="mb-2"
              type="submit"
              color="primary"
              block
              flat
              @click="handleDownload()"
            >
              Download
            </VBtn>
            <VBtn :disabled="isLoading" color="grey-100" block flat @click="handleClose()">Cancel</VBtn>
          </VCol>
        </VCardText>
      </VCardItem>
    </VCard>
  </VDialog>
</template>

<script>
import { mapActions } from 'vuex';
// Service
import documentsService from '@/services/documents-service/documents.service';
//Text
import { errorViewDocumentText, errorDownloadDocumentText } from '@/text/snackbarsText';
export default {
  emits: ['update:modelValue'],
  props: {
    modelValue: Boolean,
    documentId: String
  },
  data() {
    return {
      isLoading: true,
      isDownloadLoading: false,
      documentUrl: null,
      fileType: '',
      fileTitle: ''
    };
  },
  computed: {
    isShowModal() {
      if (this.modelValue) this.getDocumentData();
      return this.modelValue;
    },
    isPdf() {
      return this.fileType === 'application/pdf';
    },
    isImage() {
      return this.fileType.startsWith('image/');
    }
  },
  methods: {
    ...mapActions('main', ['showSnackbar', 'startLoading', 'stopLoading']),
    getMimeType(fileType = '') {
      switch (fileType.toUpperCase()) {
        case 'PDF':
          return 'application/pdf';
        case 'PNG':
          return 'image/png';
        case 'JPEG':
        case 'JPG':
          return 'image/jpeg';
        case 'GIF':
          return 'image/gif';
        case 'TXT':
          return 'text/plain';
        default:
          return 'application/octet-stream';
      }
    },
    async getDocumentData() {
      if (!this.documentId) return;
      this.isLoading = true;

      try {
        const { base64Data, fileType, fileTitle } = await documentsService.downloadDocument({
          documentId: this.documentId
        });

        const mime = this.getMimeType(fileType ?? '');

        //  Blob URL
        const byteCharacters = atob(base64Data);
        const byteNumbers = new Array(byteCharacters.length);
        for (let i = 0; i < byteCharacters.length; i++) {
          byteNumbers[i] = byteCharacters.charCodeAt(i);
        }
        const byteArray = new Uint8Array(byteNumbers);
        const blob = new Blob([byteArray], { type: mime });
        this.documentUrl = URL.createObjectURL(blob);

        this.fileType = mime;
        this.fileTitle = fileTitle || 'document';
      } catch (err) {
        console.error(err);
        this.showSnackbar({ text: errorViewDocumentText, color: 'red' });
        this.handleClose();
      } finally {
        this.isLoading = false;
      }
    },

    handleDownload() {
      if (!this.documentUrl) return;

      this.isDownloadLoading = true;

      try {
        const link = document.createElement('a');
        link.href = this.documentUrl;

        const fileName = this.fileTitle || 'document';
        link.download = fileName;

        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      } catch (err) {
        console.error(err);
        this.showSnackbar({
          text: errorDownloadDocumentText,
          color: 'red'
        });
      } finally {
        this.isDownloadLoading = false;
      }
    },

    handleClose() {
      this.fileTitle = '';
      this.fileType = '';
      if (this.documentUrl) {
        URL.revokeObjectURL(this.documentUrl);
        this.documentUrl = null;
      }
      this.$emit('update:modelValue', false);
    }
  }
};
</script>
